/**
 * UndoManager — Command pattern para permitir revertir decisiones clínicas.
 *
 * Un Command expone do() / undo(). El manager mantiene dos stacks (undo/redo).
 * Al ejecutar un nuevo comando se limpia el redo stack (comportamiento típico).
 *
 * Casos pensados para revertir:
 *   - Revelar un nodo (findingReveal): al undo, sacar el nodo de state.revealedFindings.
 *   - Setear hipótesis (hypothesisSet): al undo, volver a la hipótesis previa.
 *
 * Teclas Ctrl/Cmd+Z (undo) y Ctrl/Cmd+Shift+Z o Ctrl+Y (redo) se enganchan
 * al window. Se ignoran cuando el foco está en inputs.
 */

import state from './state';

export interface Command {
  label: string;
  do(): void;
  undo(): void;
}

const MAX_HISTORY = 50;

export class UndoManager {
  private undoStack: Command[] = [];
  private redoStack: Command[] = [];
  private listeners = new Set<() => void>();

  /** Ejecuta y pushea el comando. Limpia el redo stack. */
  execute(cmd: Command): void {
    cmd.do();
    this.undoStack.push(cmd);
    if (this.undoStack.length > MAX_HISTORY) this.undoStack.shift();
    this.redoStack = [];
    this.notify();
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  undo(): Command | null {
    const cmd = this.undoStack.pop();
    if (!cmd) return null;
    cmd.undo();
    this.redoStack.push(cmd);
    this.notify();
    return cmd;
  }

  redo(): Command | null {
    const cmd = this.redoStack.pop();
    if (!cmd) return null;
    cmd.do();
    this.undoStack.push(cmd);
    this.notify();
    return cmd;
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.notify();
  }

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  private notify(): void {
    this.listeners.forEach(fn => fn());
  }
}

export const undoManager = new UndoManager();

// ──────────────────────────────────────────────────────────────────────
// Commands reutilizables
// ──────────────────────────────────────────────────────────────────────

/**
 * Revela un nodo (equivalente a state.emitFindingRevealed) pero permite undo.
 * Si el nodo ya estaba revelado, no agrega al historial (idempotente).
 */
export function revealFindingCommand(nodeId: string, source: 'click' | 'exam' | 'gesture' | 'timeline' = 'click'): Command | null {
  if (state.revealedFindings.has(nodeId)) return null;
  return {
    label: `Revelar ${nodeId}`,
    do() {
      state.emitFindingRevealed(nodeId, source);
    },
    undo() {
      state.revealedFindings.delete(nodeId);
      state.revealedByAction.delete(nodeId);
    },
  };
}

/** Setea una hipótesis recordando la anterior. */
export function setHypothesisCommand(
  caseId: string,
  dxName: string,
  dxCode: string,
  probability: number,
  confirmed: boolean
): Command {
  const previous = state.currentHypothesis ? { ...state.currentHypothesis } : null;
  return {
    label: `Hipótesis: ${dxName}`,
    do() {
      state.setHypothesis(caseId, dxName, dxCode, probability, confirmed);
    },
    undo() {
      if (previous) {
        state.setHypothesis(previous.caseId, previous.dxName, previous.dxCode, previous.probability, previous.confirmed);
      } else {
        state.currentHypothesis = null;
      }
    },
  };
}

// Atajos de teclado globales
export function bindUndoHotkeys(): void {
  window.addEventListener('keydown', (e) => {
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    const meta = e.ctrlKey || e.metaKey;
    if (!meta) return;
    const key = e.key.toLowerCase();
    if (key === 'z' && !e.shiftKey) {
      e.preventDefault();
      undoManager.undo();
    } else if ((key === 'z' && e.shiftKey) || key === 'y') {
      e.preventDefault();
      undoManager.redo();
    }
  });
}
